// The company's list of standard inductions (round 37).
//
// CompetencyCodesPage's twin, minus the short code and the kind: an induction
// is named in full and "who issues this" is not a question anyone asks of
// one. Same manager-level door, same two tabs, same archive-over-delete rules
// — the reasoning lives in that file's header and in migration 036.
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { inductionCodes, validityLabel, type InductionCode } from '../peopleApi';
import { Banner, Field } from '../ui';
import { ActionMenu, Modal, PageHeader, useConfirm } from '../components/ui';
import { useTabParam } from '../lib/useTabParam';

const TABS = ['active', 'archived'] as const;

export function InductionCodesPage() {
  const confirm = useConfirm();
  const [codes, setCodes] = useState<InductionCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<InductionCode | 'new' | null>(null);
  const [tab, setTab] = useTabParam(TABS, 'active');

  const load = useCallback(async () => {
    try {
      const result = await inductionCodes.list({ includeArchived: true });
      setCodes(result.codes);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the inductions.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const all = codes ?? [];
  const active = all.filter((c) => !c.archived);
  const archived = all.filter((c) => c.archived);
  const showing = tab === 'archived' ? archived : active;

  async function setArchived(code: InductionCode, archive: boolean) {
    try {
      await inductionCodes.archive(code.id, archive);
      setNotice(archive ? `${code.name} archived.` : `${code.name} restored.`);
      setError(null);
      // Follow the row to the tab it just moved to.
      setTab(archive ? 'archived' : 'active');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not do that.');
    }
  }

  async function remove(code: InductionCode) {
    const ok = await confirm({
      title: `Delete ${code.name}?`,
      message:
        'If anybody holds a record against it the delete is refused, and you can archive it ' +
        'instead; archiving keeps it as the label on the old records and stops it being ' +
        'offered on new ones.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await inductionCodes.remove(code.id);
      setNotice(`${code.name} deleted.`);
      setError(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that.');
      setNotice(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Inductions"
        subtitle="The standard inductions your people sit. Used on their records."
        actions={
          <button type="button" className="btn-primary" onClick={() => setEditing('new')}>
            Add induction
          </button>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
            className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
              tab === key
                ? 'border-brand-700 font-medium text-brand-800'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {key === 'active' ? 'Active' : 'Archived'}
            <span className="ml-1 text-xs text-gray-500">
              {key === 'active' ? active.length : archived.length}
            </span>
          </button>
        ))}
      </div>

      <section className="panel">
        {codes === null ? (
          <p className="muted">Loading…</p>
        ) : showing.length === 0 ? (
          <p className="muted">
            {tab === 'archived'
              ? 'Nothing archived. Archiving an induction keeps it on the records that hold it and stops it being offered on new ones.'
              : 'No inductions yet. Add the ones your people sit; an induction on somebody’s record takes its name from one of these.'}
          </p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {showing.map((code) => (
              <li
                key={code.id}
                className="flex items-start justify-between gap-3 border-t border-gray-200 pt-2 first:border-0 first:pt-0"
              >
                <div className="min-w-0">
                  <div className="font-medium">{code.name}</div>
                  <div className="text-sm text-gray-600">
                    {validityLabel(code.validityMonths)}
                    {code.description ? ` · ${code.description}` : ''}
                  </div>
                </div>
                <ActionMenu
                  items={[
                    { label: 'Edit', onClick: () => setEditing(code) },
                    code.archived
                      ? { label: 'Restore', onClick: () => void setArchived(code, false) }
                      : { label: 'Archive', onClick: () => void setArchived(code, true) },
                    { label: 'Delete', tone: 'danger', onClick: () => void remove(code) },
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <InductionModal
          code={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setNotice(message);
            setError(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function InductionModal({
  code,
  onClose,
  onSaved,
}: {
  code: InductionCode | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(code?.name ?? '');
  const [validity, setValidity] = useState(
    code?.validityMonths === null || code?.validityMonths === undefined
      ? ''
      : String(code.validityMonths)
  );
  const [description, setDescription] = useState(code?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    const body = {
      name: name.trim(),
      description: description.trim() || null,
      validityMonths: validity === '' ? null : Number(validity),
    };

    try {
      if (code) {
        await inductionCodes.update(code.id, body);
        onSaved('Saved.');
      } else {
        await inductionCodes.create(body);
        onSaved(`${body.name} added.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={code ? `Edit ${code.name}` : 'Add induction'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="induction-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="induction-form" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}

        <Field
          label="Name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          hint="Site induction, contractor induction, a client’s annual induction…"
        />

        <Field
          label="Valid for (months)"
          name="validityMonths"
          type="number"
          value={validity}
          onChange={setValidity}
          error={fields.validityMonths}
          required={false}
          hint="Leave blank if it doesn’t expire. This only pre-fills the expiry date on a record; you can always change it there."
        />

        <label htmlFor="induction-description" className="block">
          Description
          <textarea
            id="induction-description"
            className="form-input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </form>
    </Modal>
  );
}
